import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Button, IconButton, Box, Alert } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import { useAuth } from './AuthContext';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import { get, post } from './apiClient';

export default function Layout({ title, children }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [billingStatus, setBillingStatus] = useState({ hasActiveSubscription: true });
  const [loadingBilling, setLoadingBilling] = useState(true);
  const [billingError, setBillingError] = useState(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  useEffect(() => {
    const checkBillingStatus = async () => {
      if (currentUser) {
        try {
          setLoadingBilling(true);
          const status = await get('/billing/status');
          setBillingStatus(status);
        } catch (error) {
          console.error("Failed to fetch billing status:", error);
          setBillingError("Could not verify subscription status.");
          setBillingStatus({ hasActiveSubscription: true }); // Fail open
        } finally {
          setLoadingBilling(false);
        }
      }
    };
    checkBillingStatus();
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleBillingClick = async () => {
    setIsCreatingSession(true);
    setBillingError(null);
    try {
      const { url } = await post('/billing/create-checkout-session', {
        success_url: `${window.location.origin}/dashboard?payment_success=true`,
        cancel_url: `${window.location.origin}/dashboard?payment_cancel=true`,
      });
      window.location.href = url;
    } catch (error) {
      console.error("Failed to create checkout session:", error);
      setBillingError("Could not open the billing page. Please try again.");
      setIsCreatingSession(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="home"
            component={RouterLink}
            to="/dashboard"
            sx={{ mr: 2 }}
          >
            <HomeIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          <Button color="inherit" component={RouterLink} to="/profile">
            {currentUser?.email}
          </Button>
          <Button color="inherit" onClick={handleLogout}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <main>
        {billingError && <Alert severity="error" sx={{ m: 2 }}>{billingError}</Alert>}
        {!loadingBilling && !billingStatus.hasActiveSubscription && (
          <Alert severity="warning" sx={{ m: 2 }}>
            You do not have an active subscription. Please add a payment method to be able to create jobs.
            <Button
              color="inherit"
              size="small"
              sx={{ ml: 2 }}
              variant="outlined"
              onClick={handleBillingClick}
              disabled={isCreatingSession}
            >
              {isCreatingSession ? 'Redirecting...' : 'Add Payment Method'}
            </Button>
          </Alert>
        )}
        {children}
      </main>
    </Box>
  );
}
